import {
    ActionControllerApi,
    Configuration,
} from "@inode/pilot-client-typescript-fetch";
import { asyncPoll } from "#/util/asyncPoll";
import {
    Action,
    DatasourceEnum,
    Interpretation,
    OperatorInvocation,
} from "#services/pilotService/types";
import { baseCfg } from "#services/pilotService/baseCfg";
import {
    PilotFacetBody,
    PilotFilterBody,
    PilotSupersetBody,
} from "#store/actions";

export class PilotActionService {
    private actions: ActionControllerApi;
    private url: string;
    private config: Configuration;
    private userId: string;

    constructor(url: string, userId: string) {
        this.url = url;
        this.userId = userId;
        this.config = new Configuration(baseCfg(url, userId));
        this.actions = new ActionControllerApi(this.config);
    }

    list(): Promise<Array<Action>> {
        return this.actions
            .listActions({
                X_Reference_ID: this.userId,
            })
            .then((response: Array<Action>) => {
                return response;
            })
            .catch(err => {
                console.log("Failed to list actions", err);
                return Promise.reject(err);
            });
    }

    get(id: number): Promise<Action> {
        return asyncPoll<Action>(
            async () => {
                const response = await this.actions.getActionRaw({
                    id,
                    X_Reference_ID: this.userId,
                });
                try {
                    switch (response.raw.status) {
                        case 200: {
                            // TODO dispatch "poller is done" event.

                            return Promise.resolve({
                                done: true,
                                data: await response.value(),
                            });
                        }
                        case 201:
                        case 202:
                            // TODO dispatch "poller is repeating" event.
                            // keep polling
                            return Promise.resolve({ done: false });
                        default:
                            // TODO dispatch "poller is failed" event.

                            return Promise.reject();
                    }
                } catch (err) {
                    console.log("Failed to get action", err);
                    return Promise.reject(err);
                }
            },
            500,
            15000
        );
    }

    getOperatorInvocations(id: number): Promise<Array<OperatorInvocation>> {
        return this.actions.getActionOperatorInvocations({
            id,
            X_Reference_ID: this.userId,
        });
    }

    getInterpretations(id: number): Promise<Array<Interpretation>> {
        return this.actions.getActionInterpretations({
            id,
            X_Reference_ID: this.userId,
        });
    }

    nl2ql(
        query: string,
        datasource: DatasourceEnum,
        maxInterpretations = 5
    ): Promise<Action> {
        let put: Promise<Action>;
        switch (datasource) {
            case DatasourceEnum.sdss:
            case DatasourceEnum.cordis:
            case DatasourceEnum.oncomx:
            case DatasourceEnum.world_cup:
                put = this.actions.putNl2sqlAction({
                    Nl2SqlRequestBody: {
                        dataset: datasource,
                        query,
                    },
                    X_Reference_ID: this.userId,
                    maxInterpretations: maxInterpretations,
                });
                break;
            case DatasourceEnum.sdss_sparql:
            case DatasourceEnum.cordis_sparql:
            case DatasourceEnum.oncomx_sparql:
                put = this.actions.putNl2sparqlAction({
                    X_Reference_ID: this.userId,
                    Nl2SparqlRequestBody: {
                        query,
                        dataset: datasource,
                        maxInterpretations,
                    },
                });
                break;
            default:
                throw new Error("Illegal datasoure provided" + datasource);
        }
        return put
            .then(response => {
                console.log("nl2sql response", response);
                return this.get(response.id);
            })
            .catch(err => {
                console.log("Failed to trigger nl2sql action", err);
                return Promise.reject(err);
            });
    }

    sql2data(query: string, datasource: DatasourceEnum): Promise<Action> {
        return this.actions
            .putQl2dataAction({
                Nl2SqlRequestBody: {
                    dataset: datasource,
                    query,
                },
                X_Reference_ID: this.userId,
            })
            .then(response => {
                console.log("sql2data response", response);
                return this.get(response.id);
            })
            .catch(err => {
                console.log("Failed to trigger sql2data action", err);
                return Promise.reject(err);
            });
    }

    /**
     * Runs the cnrs operator which adds a where clause.
     * @param sql
     * @param column
     * @param value
     * @param operator
     * @param datasource
     * @param maxInterpretations
     */
    exploreByFilter({
        column,
        value,
        query,
        operator = "=",
        datasource = DatasourceEnum.cordis,
        maxInterpretations = 10,
    }: PilotFilterBody): Promise<Action> {
        let decodedValue = JSON.parse(String(value));
        return this.actions.putFilterByRangeAction({
            X_Reference_ID: this.userId,
            FilterByRangeRequestBody: {
                column: column,
                dataset: datasource,
                query: query,
                value: !Array.isArray(decodedValue)
                    ? String(decodedValue)
                    : undefined,
                values: Array.isArray(decodedValue)
                    ? (decodedValue as number[])
                    : undefined,
            },
        });
        // return this.actions.putPipelineFilterAction({
        //     X_Reference_ID: this.userId,
        //     ByFilterRequest: {
        //         filter: {
        //             leftOperand: {
        //                 value: column,
        //                 type: "Column",
        //             },
        //             operator: operator,
        //             rightOperand: {
        //                 value: value,
        //                 type: isNaN(Number(value)) ? "String" : "Number",
        //             },
        //         },
        //         maxInterpretations: maxInterpretations,
        //         datasource: datasource,
        //         sqlQuery: query,
        //     },
        // });
    }

    exploreByFacet({
        column,
        query,
        datasource = DatasourceEnum.cordis,
        maxInterpretations = 10,
    }: PilotFacetBody): Promise<Action> {
        return this.actions.putPipelineFacetAction({
            X_Reference_ID: this.userId,
            ByFacetRequest: {
                attributes: [column],
                maxInterpretations: maxInterpretations,
                datasource: datasource,
                sqlQuery: query,
            },
        });
    }

    exploreBySuperset({
        query,
        datasource = DatasourceEnum.cordis,
        maxInterpretations = 10,
    }: PilotSupersetBody): Promise<Action> {
        return this.actions.putPipelineSupersetAction({
            X_Reference_ID: this.userId,
            BySupersetRequest: {
                maxInterpretations: maxInterpretations,
                datasource: datasource,
                sqlQuery: query,
            },
        });
    }

    recommendations(
        query: string,
        datasource: DatasourceEnum,
        maxRecommendations = 0
    ): Promise<Action> {
        return this.actions
            .putRecommendationsAction({
                ByRecommendationRequestBody: {
                    dataset: datasource,
                    query,
                    maxRecommendations,
                },
                X_Reference_ID: this.userId,
            })
            .then(response => {
                console.log("recommendations response", response);
                return this.get(response.id);
            })
            .catch(err => {
                console.log("Failed to trigger recommendations action", err);
                return Promise.reject(err);
            });
    }

    writeToLog(event: string, payload: any): void {
        this.actions
            .putLogAction({
                LoggingRequest: {
                    timestamp: new Date(),
                    rid: this.userId,
                    emitter: "pilot-frontend",
                    event: event,
                    payload: payload,
                },
            })
            .then(response => {
                console.log("writeToLog succeeded");
            })
            .catch(err => {
                console.log("Failed to trigger writeToLog action", err);
            });
    }
}
